import { useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, Megaphone, Clock, Heart, Sparkles, CalendarDays, Quote, ChevronDown } from "lucide-react";

import { useSite } from "@/context/SiteContext";
import { useQuery } from "@tanstack/react-query";
import { api, API } from "@/lib/api";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import ServiceTimesGrid from "@/components/ServiceTimesGrid";
import HeroVideos from "@/components/HeroVideos";
import GalleryPreview from "@/components/GalleryPreview";
import HomeCarousel from "@/components/HomeCarousel";
import PrayerForm from "@/components/PrayerForm";

const FOUNDER_IMG =
  "https://customer-assets.emergentagent.com/job_glowing-grace-chapel/artifacts/n0qitlne_bishopinblue.jpg";

const THEME_FALLBACK =
  "https://images.unsplash.com/photo-1507692049790-de58290a4334?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100";

const SATIN_VIDEO =
  "https://customer-assets.emergentagent.com/job_glowing-grace-chapel/artifacts/dxsdu29g_YTDown_YouTube_Luxurious-Red-Satin-Fabric-Free-Backgrou_Media_06XCqFX1wq0_001_1080p.mp4";

const LOGO = `${process.env.PUBLIC_URL}/logo.png`;
const APP_STORE_URL = "https://apps.apple.com/gb/app/winners-dp/id6469002825";
const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.xcelcmp.app&pcampaignid=web_share";

function AppleIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M16.365 1.43c0 1.14-.42 2.2-1.13 3.02-.86.99-2.27 1.76-3.42 1.66-.14-1.13.45-2.32 1.12-3.06.78-.86 2.16-1.5 3.43-1.62zM20.5 17.36c-.55 1.27-.82 1.84-1.53 2.96-.99 1.57-2.39 3.53-4.12 3.54-1.54.02-1.94-1-4.03-.99-2.09.01-2.53 1.01-4.07.99-1.73-.02-3.05-1.78-4.04-3.35C-.02 16.7-.32 11.27 2.07 8.7c1.06-1.16 2.5-1.84 4.06-1.84 1.6 0 2.6.99 3.93.99 1.28 0 2.06-1 3.92-1 1.43 0 2.94.78 4.02 2.13-3.53 1.93-2.95 6.98.5 8.38z" />
    </svg>
  );
}

function GooglePlayIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M3.6 2.1C3.24 2.3 3 2.68 3 3.12v17.76c0 .44.24.82.6 1.02L13.3 12 3.6 2.1zm11.1 11.3l2.6 2.6-9.6 5.49 7-8.09zm0-2.8L7.7 2.51l9.6 5.49-2.6 2.6zm3.7 1.4 2.9 1.66c.66.38.66 1.34 0 1.72l-2.9 1.66L15.5 12l2.9-2.9z" />
    </svg>
  );
}

const catBadge = {
  Service: "bg-chapel-red/15 text-chapel-glow",
  Event: "bg-amber-400/15 text-amber-300",
  Prayer: "bg-sky-400/15 text-sky-300",
  Announcement: "bg-emerald-400/15 text-emerald-300",
};

export default function Home() {
  const { content } = useSite();
  const c = content || {};
  const themeImg = c.theme_image_path
    ? `${API}/content/theme-image?v=${encodeURIComponent(c.theme_image_updated || "")}`
    : c.theme_image || THEME_FALLBACK;
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const { data: announcements = [] } = useQuery({
    queryKey: ["announcements"],
    queryFn: async () => (await api.get("/announcements")).data,
  });

  const location = useLocation();
  useEffect(() => {
    if (location.hash !== "#give") return;
    let tries = 0;
    const timer = setInterval(() => {
      const el = document.getElementById("give");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      if (++tries >= 6) clearInterval(timer);
    }, 300);
    return () => clearInterval(timer);
  }, [location]);

  return (
    <div data-testid="home-page">
      <SEO
        title="Home"
        description="Welcome to Winners Chapel International Colchester. Come and experience the Divine Touch of God."
      />

      {/* ───────── HERO ───────── */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden bg-[#0B0B0F]">
        <motion.div style={{ y: bgY }} className="absolute inset-0 z-0">
          {/* Fallback image shows while videos buffer */}
          <img
            src="https://images.unsplash.com/photo-1579975096649-e773152b04cb?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <HeroVideos />
          {/* Minimal overlay for legibility + soft fade into the white page */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/25 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-[#1a0609]" />
        </motion.div>

        <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pt-24">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-xs font-semibold tracking-wider uppercase text-white mb-6"
          >
            <Sparkles className="w-3.5 h-3.5 text-chapel-glow" />
            A branch of Living Faith Church Worldwide
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-heading font-extrabold text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tighter max-w-4xl text-glow"
          >
            Winners Chapel International{" "}
            <span className="text-chapel-red">Colchester</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="mt-6 text-lg sm:text-2xl text-white/90 max-w-2xl font-light"
          >
            {c.tagline || "Come and experience the Divine Touch of God."}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-plan-visit"
              className="group inline-flex items-center gap-2 bg-chapel-red hover:bg-chapel-glow text-white font-semibold rounded-full px-8 py-4 text-base transition-all duration-300 red-glow"
            >
              Plan Your Visit
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/service-times"
              data-testid="hero-service-times"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/25 hover:border-white/50 text-white font-semibold rounded-full px-8 py-4 text-base transition-all duration-300"
            >
              <Clock className="w-5 h-5" />
              Service Times
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: "-50%", y: -6 }}
          animate={{ opacity: 1, x: "-50%", y: 0 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-7 left-1/2 z-10 flex flex-col items-center gap-1.5"
        >
          <span className="text-[10px] font-semibold tracking-[0.3em] uppercase text-white/60 mb-0.5">Scroll</span>
          <div className="relative grid">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="col-start-1 row-start-1"
                style={{ marginTop: i * 7 }}
                animate={{ opacity: [0, 1, 0], y: [0, 6, 12] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: i * 0.18 }}
              >
                <ChevronDown
                  className="w-6 h-6 text-chapel-red"
                  strokeWidth={3}
                  style={{ filter: "drop-shadow(0 0 6px rgba(225,6,0,0.7))" }}
                />
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ───────── WELCOME ───────── */}
      <section className="relative py-24 sm:py-32 bg-sec-1">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <span className="text-xs font-bold tracking-[0.25em] uppercase text-chapel-red">
              {c.welcome_title || "Welcome Home"}
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-heading font-extrabold text-white text-3xl sm:text-5xl tracking-tight">
              You belong here.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-7 text-lg sm:text-xl text-zinc-300 leading-relaxed font-light">
              {c.welcome_message}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 grid gap-5 sm:grid-cols-3 text-left">
              {[
                { icon: Heart, title: "Warm Community", text: "A family that genuinely cares for one another." },
                { icon: Sparkles, title: "Spirit-Filled Worship", text: "Encounter the presence of God every service." },
                { icon: Megaphone, title: "The Word of Faith", text: "Practical truth that transforms your everyday life." },
              ].map((f, i) => (
                <div key={i} className="glass rounded-2xl p-6">
                  <span className="w-11 h-11 grid place-items-center rounded-xl bg-chapel-red/10 mb-3">
                    <f.icon className="w-6 h-6 text-chapel-red" />
                  </span>
                  <h3 className="font-heading font-bold text-white text-lg mb-1">{f.title}</h3>
                  <p className="text-zinc-300 text-sm leading-relaxed">{f.text}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────── ABOUT US ───────── */}
      <section className="relative py-24 sm:py-32 bg-sec-2">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="relative">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden glass p-2 max-w-md">
                <img src={FOUNDER_IMG} alt="Bishop David O. Oyedepo, Founder & President of LFCWW" className="w-full h-full object-cover rounded-2xl" />
                <div className="absolute bottom-4 left-4 right-4 sm:left-6 sm:right-6">
                  <div className="glass-strong rounded-2xl px-4 py-3 border border-white/20">
                    <p className="font-heading font-bold text-white text-sm">Bishop David O. Oyedepo</p>
                    <p className="text-zinc-400 text-xs">Founder & President, LFCWW</p>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 w-44 h-44 rounded-full blur-[90px] pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(225,6,0,0.4), transparent 70%)" }} />
              <div className="absolute -top-6 -left-6 w-32 h-32 rounded-full blur-[80px] pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(255,138,0,0.3), transparent 70%)" }} />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div>
              <span className="text-xs font-bold tracking-[0.25em] uppercase text-chapel-red">About Us</span>
              <h2 className="mt-3 font-heading font-extrabold text-white text-3xl sm:text-5xl tracking-tight">
                A Place to Belong
              </h2>
              <p className="mt-6 text-lg text-zinc-300 leading-relaxed">
                Winners Chapel International Colchester is a vibrant, faith-filled community dedicated to the preaching
                of the Word of Faith. We believe in the power of God's Word to transform lives and build champions.
                Whether you're new to faith or a seasoned believer, you'll find a warm and welcoming family here.
              </p>
              <Link
                to="/about"
                className="mt-8 inline-flex items-center gap-2 bg-chapel-red hover:bg-chapel-glow text-white font-semibold rounded-full px-7 py-3.5 transition-all duration-300 red-glow"
              >
                Learn more about us <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────── SERVICE TIMES PREVIEW ───────── */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover opacity-40 transform-gpu"
          src={SATIN_VIDEO}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          disablePictureInPicture
          aria-hidden="true"
          data-testid="service-times-bg-video"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#160509]/94 via-[#1a0609]/91 to-[#120407]/96 backdrop-blur-[3px]" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
            <div>
              <Reveal>
                <span className="text-xs font-bold tracking-[0.25em] uppercase text-chapel-red">Gather With Us</span>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-3 font-heading font-extrabold text-white text-3xl sm:text-5xl tracking-tight">
                  Service Times
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <Link to="/service-times" className="inline-flex items-center gap-2 text-zinc-200 hover:text-chapel-red transition-colors font-medium">
                View all services <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
          <ServiceTimesGrid services={c.service_times || []} special={c.special_services} sunday={c.sunday_services} />
        </div>
      </section>

      {/* ───────── ANNOUNCEMENT CAROUSEL ───────── */}
      <HomeCarousel />

      {/* ───────── ANNOUNCEMENTS ───────── */}
      {announcements.length > 0 && (
        <section className="relative py-24 sm:py-32 bg-sec-2 overflow-hidden">
          <div className="absolute top-0 right-0 w-[40vw] h-[40vw] rounded-full blur-[130px] pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(225,6,0,0.12), transparent 70%)" }} />
          <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
              <div>
                <Reveal>
                  <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.25em] uppercase text-chapel-red">
                    <Megaphone className="w-4 h-4" /> Latest News
                  </span>
                </Reveal>
                <Reveal delay={0.1}>
                  <h2 className="mt-3 font-heading font-extrabold text-white text-3xl sm:text-5xl tracking-tight">
                    Announcements
                  </h2>
                </Reveal>
              </div>
              <Reveal delay={0.2}>
                <Link to="/calendar" className="inline-flex items-center gap-2 text-zinc-200 hover:text-chapel-red transition-colors font-medium">
                  View calendar <ArrowRight className="w-4 h-4" />
                </Link>
              </Reveal>
            </div>

            <div className="grid gap-5 md:grid-cols-3">
              {announcements.slice(0, 6).map((a, i) => (
                <Reveal key={a.id} delay={i * 0.1}>
                  <article className="group relative glass rounded-2xl p-7 h-full overflow-hidden transition-all duration-300 hover:-translate-y-1.5">
                    <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-chapel-red to-chapel-glow" />
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4 ${catBadge[a.category] || catBadge.Announcement}`}>
                      {a.category}
                    </span>
                    <p className="flex items-center gap-1.5 text-xs text-zinc-400 mb-2">
                      <CalendarDays className="w-3.5 h-3.5" />
                      {new Date(a.date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                    </p>
                    <h3 className="font-heading font-bold text-white text-xl tracking-tight group-hover:text-chapel-red transition-colors">
                      {a.title}
                    </h3>
                    <p className="mt-3 text-zinc-300 text-sm leading-relaxed line-clamp-4">{a.body}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ───────── THEME OF THE MONTH ───────── */}
      <section className="relative py-24 sm:py-32 bg-gradient-to-br from-[#2a0a10] to-[#120407] overflow-hidden">
        <div className="absolute -top-1/4 -left-10 w-[45vw] h-[45vw] rounded-full blur-[140px] pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(225,6,0,0.4), transparent 70%)" }} />
        <div className="absolute bottom-0 right-0 w-[40vw] h-[40vw] rounded-full blur-[140px] pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(255,138,0,0.25), transparent 70%)" }} />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="relative">
              <div className="aspect-[16/11] rounded-3xl overflow-hidden border border-white/10">
                <img
                  src={themeImg}
                  alt={c.theme_title || "Theme of the month"}
                  onError={(e) => { if (e.currentTarget.src !== THEME_FALLBACK) e.currentTarget.src = THEME_FALLBACK; }}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-chapel-red/20 border border-chapel-red/30 text-chapel-glow text-xs font-bold tracking-wider uppercase mb-5">
                <Sparkles className="w-3.5 h-3.5" /> Theme of the Month
              </span>
              <h2 className="font-heading font-extrabold text-white text-3xl sm:text-5xl tracking-tight leading-tight">
                {c.theme_title || "Our Year of Unending Testimonies"}
              </h2>
              {c.theme_scripture && (
                <div className="mt-6 flex gap-3">
                  <Quote className="w-7 h-7 text-chapel-red shrink-0" />
                  <p className="text-chapel-paper/85 text-lg italic font-light">{c.theme_scripture}</p>
                </div>
              )}
              {c.theme_text && (
                <p className="mt-6 text-chapel-paper/70 leading-relaxed">{c.theme_text}</p>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────── GALLERY PREVIEW ───────── */}
      <GalleryPreview />

      {/* ───────── MAP SNIPPET ───────── */}
      <section className="relative py-24 sm:py-32 bg-sec-2">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <div>
              <span className="text-xs font-bold tracking-[0.25em] uppercase text-chapel-red">Find Us</span>
              <h2 className="mt-3 font-heading font-extrabold text-white text-3xl sm:text-5xl tracking-tight">
                Come and visit
              </h2>
              <p className="mt-5 text-zinc-300 leading-relaxed flex items-start gap-2">
                <MapPin className="w-5 h-5 text-chapel-red shrink-0 mt-1" />
                {c.address}
              </p>
              <Link
                to="/contact"
                className="mt-8 inline-flex items-center gap-2 bg-chapel-red hover:bg-chapel-glow text-white font-semibold rounded-full px-7 py-3.5 transition-all duration-300 red-glow"
              >
                Get Directions <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-3xl overflow-hidden glass p-2">
              <iframe
                title="Church location map"
                src={c.map_embed}
                className="w-full h-[320px] rounded-2xl border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────── GIVE ───────── */}
      <section id="give" data-testid="give-section" className="relative py-20 sm:py-28 bg-sec-1">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div
              className="relative overflow-hidden rounded-3xl p-8 sm:p-12 text-center border border-white/10"
              style={{ background: "linear-gradient(135deg,#E10600 0%,#9e0400 58%,#6a0a0a 100%)" }}
            >
              <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full blur-[90px] pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(255,160,0,0.4), transparent 70%)" }} />
              <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "26px 26px" }} />

              <div className="relative">
                <span className="inline-flex items-center gap-2 text-white/90 text-xs font-bold tracking-[0.2em] uppercase">
                  <Heart className="w-4 h-4" /> Give Generously
                </span>
                <h2 className="mt-3 font-heading font-extrabold text-white text-3xl sm:text-5xl tracking-tight text-glow">
                  Tithes &amp; Offerings
                </h2>
                <p className="mt-4 text-white/90 max-w-xl mx-auto leading-relaxed">
                  Give your tithes, offerings and seeds easily and securely through the
                  <span className="font-semibold"> Winners DP App</span> — available on both Android and iOS.
                </p>

                <div className="mt-8 flex flex-col items-center">
                  <span className="w-20 h-20 grid place-items-center rounded-2xl bg-white shadow-lg p-2.5">
                    <img src={LOGO} alt="Winners DP App" className="w-full h-full object-contain" />
                  </span>
                  <p className="mt-3 font-heading font-bold text-white text-lg">Winners DP App</p>
                </div>

                <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center items-center">
                  <a
                    href={GOOGLE_PLAY_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="give-googleplay"
                    className="group flex items-center gap-3 bg-black hover:bg-zinc-900 text-white rounded-2xl px-6 py-3 w-60 transition-colors duration-300"
                  >
                    <GooglePlayIcon className="w-7 h-7 shrink-0" />
                    <span className="text-left leading-tight">
                      <span className="block text-[10px] uppercase tracking-wide text-white/70">Get it on</span>
                      <span className="block text-lg font-semibold">Google Play</span>
                    </span>
                  </a>
                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="give-appstore"
                    className="group flex items-center gap-3 bg-black hover:bg-zinc-900 text-white rounded-2xl px-6 py-3 w-60 transition-colors duration-300"
                  >
                    <AppleIcon className="w-7 h-7 shrink-0" />
                    <span className="text-left leading-tight">
                      <span className="block text-[10px] uppercase tracking-wide text-white/70">Download on the</span>
                      <span className="block text-lg font-semibold">App Store</span>
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────── PRAYER REQUEST ───────── */}
      <PrayerForm />

      {/* ───────── CTA BAND ───────── */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ background: "linear-gradient(120deg, #E10600 0%, #B00500 55%, #7a0a0a 100%)" }}>
        <div className="absolute -top-10 right-10 w-72 h-72 rounded-full blur-[100px] pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(255,138,0,0.5), transparent 70%)" }} />
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-heading font-extrabold text-white text-3xl sm:text-5xl tracking-tight text-glow">
              Your divine encounter awaits
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-white/90 text-lg max-w-2xl mx-auto font-light">
              Come and experience the Divine Touch of God. We'd love to welcome you and your family this week.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-9 flex flex-wrap gap-4 justify-center">
              <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-chapel-red font-bold rounded-full px-8 py-4 hover:bg-chapel-paper transition-all duration-300 shadow-lg">
                Plan Your Visit <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/service-times" className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/30 text-white font-semibold rounded-full px-8 py-4 hover:bg-white/20 transition-all duration-300">
                <Clock className="w-5 h-5" /> Service Times
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
